import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const POLL_INTERVAL_MS = 25_000;

const NotificationsContext = createContext(null);

function getAuth() {
  return { headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` } };
}

// Friendly display title from event_type + payload
function displayFor(n) {
  const p = n.payload || {};
  switch (n.event_type) {
    case 'lead_assigned':              return { title: 'New lead assigned to you',      body: p.client_name };
    case 'lead_reassigned':            return { title: 'Lead reassigned',                body: `${p.client_name} · ${p.previous_owner_name} → ${p.new_owner_name}` };
    case 'client_selections_submitted':return { title: 'Client selections submitted',    body: p.client_name };
    case 'lead_sent_to_production':    return { title: 'New job in production',          body: `${p.client_name} — ${p.loved_one_first_name}` };
    case 'production_task_assigned':   return { title: 'Task assigned to you',           body: `${p.task_name} · ${p.client_name}` };
    case 'production_task_blocked':    return { title: 'Task marked blocked',            body: `${p.task_name} · ${p.client_name}` };
    case 'production_task_overdue':    return { title: 'Task overdue',                   body: `${p.task_name} · ${p.client_name}` };
    case 'production_job_completed':   return { title: 'Production complete',            body: `${p.client_name} — ${p.loved_one_first_name}` };
    case 'production_job_manually_completed': return { title: 'Production complete (manual)', body: `${p.client_name} — ${p.loved_one_first_name}` };
    case 'lead_recalled_from_production': return { title: 'Lead recalled from production', body: p.client_name };
    case 'user_account_created':       return { title: 'Welcome to Heirloom Ops',        body: null };
    case 'user_password_changed':      return { title: 'Your password was changed',      body: null };
    default:                            return { title: n.message || 'Notification',     body: null };
  }
}

// Return a relative path to navigate to when a notif is clicked.
function routeFor(n, currentRole) {
  const p = n.payload || {};
  const leadPage = currentRole === 'interviewer' ? '/ops/interviewer/pipeline' : '/ops/admin/leads';
  if (p.task_id && p.job_id)  return `/ops/production/jobs?job=${p.job_id}&task=${p.task_id}`;
  if (p.job_id)               return `/ops/production/jobs?job=${p.job_id}`;
  if (p.lead_id)              return `${leadPage}?lead=${p.lead_id}`;
  return '/ops/notifications';
}

export function NotificationsProvider({ children }) {
  const { user, activeRole } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [recent, setRecent] = useState([]);
  const sessionStartRef = useRef(Date.now());
  const seenIdsRef = useRef(new Set());
  const isFirstLoadRef = useRef(true);
  const pollRef = useRef(null);

  const fetchAll = useCallback(async () => {
    if (!user) return;
    try {
      const [cnt, list] = await Promise.all([
        axios.get(`${API}/ops/notifications/unread-count`, getAuth()),
        axios.get(`${API}/ops/notifications?limit=30`, getAuth()),
      ]);
      setUnreadCount(cnt.data.count || 0);
      const items = list.data || [];
      // Toast new ones that arrived during the active session (first poll loads context only).
      if (!isFirstLoadRef.current) {
        items.forEach(n => {
          if (seenIdsRef.current.has(n.id)) return;
          const createdAt = new Date(n.created_at).getTime();
          if (createdAt >= sessionStartRef.current && !n.is_read) {
            const d = displayFor(n);
            toast(d.title, { description: d.body, duration: 5000, closeButton: true });
          }
        });
      } else {
        isFirstLoadRef.current = false;
      }
      items.forEach(n => seenIdsRef.current.add(n.id));
      setRecent(items);
    } catch {}
  }, [user]);

  useEffect(() => {
    if (!user) {
      setUnreadCount(0); setRecent([]);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      sessionStartRef.current = Date.now();
      isFirstLoadRef.current = true;
      seenIdsRef.current = new Set();
      return;
    }
    sessionStartRef.current = Date.now();
    isFirstLoadRef.current = true;
    fetchAll();
    pollRef.current = setInterval(fetchAll, POLL_INTERVAL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [user, fetchAll]);

  const markRead = useCallback(async (id) => {
    try {
      await axios.post(`${API}/ops/notifications/${id}/read`, {}, getAuth());
      setRecent(r => r.map(n => n.id === id ? { ...n, is_read: true, read: true } : n));
      setUnreadCount(c => Math.max(0, c - 1));
    } catch {}
  }, []);
  const markAllRead = useCallback(async () => {
    try {
      await axios.post(`${API}/ops/notifications/mark-all-read`, {}, getAuth());
      setRecent(r => r.map(n => ({ ...n, is_read: true, read: true })));
      setUnreadCount(0);
    } catch {}
  }, []);
  const archive = useCallback(async (id) => {
    try {
      await axios.post(`${API}/ops/notifications/${id}/archive`, {}, getAuth());
      setRecent(r => r.filter(n => n.id !== id));
      fetchAll();
    } catch {}
  }, [fetchAll]);

  const value = {
    unreadCount,
    recent,
    activeRole,
    refresh: fetchAll,
    markRead,
    markAllRead,
    archive,
    displayFor,
    routeFor: (n) => routeFor(n, activeRole),
  };
  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
}

export function useNotifications() {
  return useContext(NotificationsContext) || {
    unreadCount: 0, recent: [], refresh: () => {}, markRead: () => {}, markAllRead: () => {}, archive: () => {},
    displayFor, routeFor: (n) => routeFor(n, null),
  };
}
